import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Tag, ChevronLeft } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Price Check Modal - Customer-facing price lookup
 * 
 * Shows ONLY product name and selling price.
 * No cost prices, markup, or other sensitive info.
 * Designed to be shown to customers at the counter.
 * 
 * Supports searching by:
 * - Description (with flexible word order: "BAR ROUND" finds "ROUND BAR")
 * - PLU code
 * - Barcode
 * - Supplier PLU
 */
const PriceCheckModal = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedProduct(null);
      setHasSearched(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Flexible word-order matching
  const matchesFlexibleSearch = (product, query) => {
    const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const productName = (product.name || "").toLowerCase();
    const supplierPlu = (product.supplier_plu || "").toLowerCase();
    const pluCode = (product.plu_code || "").toLowerCase();
    const barcode = (product.barcode || "").toLowerCase();
    const barcodes = (product.barcodes || []).map(b => b.toLowerCase());
    
    // Check exact matches for PLU, barcode, supplier PLU
    if (pluCode === query.toLowerCase()) return { match: true, score: 100 };
    if (barcode === query.toLowerCase()) return { match: true, score: 100 };
    if (barcodes.includes(query.toLowerCase())) return { match: true, score: 100 };
    if (supplierPlu === query.toLowerCase()) return { match: true, score: 95 };
    
    // Check if all query words appear in product name (flexible word order)
    const allWordsMatch = queryWords.every(word => productName.includes(word));
    if (allWordsMatch) {
      // Score based on how closely the words appear together
      const wordPositions = queryWords.map(word => productName.indexOf(word));
      const spread = Math.max(...wordPositions) - Math.min(...wordPositions);
      const score = Math.max(50, 90 - spread); // Higher score for closer matches
      return { match: true, score };
    }
    
    // Check partial matches in supplier PLU
    if (supplierPlu.includes(query.toLowerCase())) return { match: true, score: 40 };
    
    return { match: false, score: 0 };
  };

  const handleSearch = async (query) => {
    if (!query || query.trim().length < 1) {
      setSearchResults([]);
      setSelectedProduct(null);
      setHasSearched(false);
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setSelectedProduct(null);
    
    try {
      // Get all products matching the query from backend
      const response = await axios.get(`${API}/products/search`, {
        params: { q: query.trim() }
      });
      
      if (response.data && response.data.length > 0) {
        // Apply flexible matching and scoring
        const scoredResults = response.data
          .map(product => {
            const { match, score } = matchesFlexibleSearch(product, query.trim());
            return { ...product, _matchScore: match ? score : 0 };
          })
          .filter(p => p._matchScore > 0)
          .sort((a, b) => b._matchScore - a._matchScore);
        
        // Check for exact PLU/barcode match - auto-select if found
        const exactMatch = scoredResults.find(p => p._matchScore >= 95);
        
        if (exactMatch && scoredResults.length === 1) {
          // Only one result with high confidence - show it directly
          setSelectedProduct(exactMatch);
          setSearchResults([]);
        } else if (scoredResults.length > 0) {
          // Multiple results - show list
          setSearchResults(scoredResults.slice(0, 20)); // Limit to 20 results
          setSelectedProduct(null);
        } else {
          setSearchResults([]);
        }
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Price check search error:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch(searchQuery);
    } else if (e.key === "Escape") {
      if (selectedProduct) {
        // Go back to results
        setSelectedProduct(null);
      } else {
        onClose();
      }
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  const handleBack = () => {
    setSelectedProduct(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const getSellingPrice = (product) => {
    return product.selling_price_incl_vat || product.retail_price_level1 || 0;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white">
        {/* Header - Clean and simple */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
          <div className="flex items-center gap-3">
            {selectedProduct ? (
              <button 
                onClick={handleBack}
                className="p-1 hover:bg-blue-500 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
            ) : (
              <Tag className="h-8 w-8" />
            )}
            <div>
              <h2 className="text-2xl font-bold">Price Check</h2>
              <p className="text-blue-100 text-sm">
                {selectedProduct ? "Product details" : "Search by description, PLU, barcode"}
              </p>
            </div>
          </div>
        </div>

        {/* Search Input - Only show when not viewing a product */}
        {!selectedProduct && (
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search by description, PLU, barcode, supplier PLU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10 h-12 text-lg"
                autoFocus
                data-testid="price-check-input"
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Tip: Search words in any order (e.g., &quot;BAR ROUND&quot; finds &quot;ROUND BAR&quot;)
            </p>
          </div>
        )}

        {/* Content Area */}
        <div className={`${selectedProduct ? 'p-6' : 'p-4'} min-h-[280px] max-h-[400px] overflow-y-auto`}>
          {searching ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <div className="animate-pulse text-lg">Searching...</div>
              </div>
            </div>
          ) : selectedProduct ? (
            // Single Product View - Customer Friendly
            <div className="text-center" data-testid="price-check-result">
              {/* Product Name - Large and Clear */}
              <div className="text-2xl font-semibold text-gray-800 mb-4 leading-tight">
                {selectedProduct.name}
              </div>
              
              {/* Price - Very Prominent */}
              <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
                <div className="text-sm text-green-600 font-medium mb-1">PRICE</div>
                <div className="text-5xl font-bold text-green-700" data-testid="price-check-price">
                  R {getSellingPrice(selectedProduct).toFixed(2)}
                </div>
                {selectedProduct.sold_per_kg && (
                  <div className="text-green-600 mt-2 text-lg">per kilogram</div>
                )}
              </div>

              {/* Unit info if applicable */}
              {selectedProduct.unit && selectedProduct.unit !== "Each" && !selectedProduct.sold_per_kg && (
                <div className="mt-3 text-gray-500">
                  Sold per {selectedProduct.unit}
                </div>
              )}
            </div>
          ) : searchResults.length > 0 ? (
            // Multiple Results List
            <div className="space-y-2" data-testid="price-check-results-list">
              <div className="text-sm text-gray-500 mb-3">
                Found {searchResults.length} product{searchResults.length > 1 ? 's' : ''} - tap to select
              </div>
              {searchResults.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  data-testid={`price-check-item-${product.plu_code}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">
                        {product.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        PLU: {product.plu_code}
                        {product.supplier_plu && ` • Supplier: ${product.supplier_plu}`}
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-lg font-bold text-green-600">
                        R {getSellingPrice(product).toFixed(2)}
                      </div>
                      {product.sold_per_kg && (
                        <div className="text-xs text-gray-400">/kg</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : hasSearched ? (
            // No Results
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500" data-testid="price-check-not-found">
                <div className="text-6xl mb-4">🔍</div>
                <div className="text-xl font-medium">No products found</div>
                <div className="text-sm mt-2">Try different keywords or ask for assistance</div>
              </div>
            </div>
          ) : (
            // Initial State
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400">
                <div className="text-6xl mb-4">📦</div>
                <div className="text-lg">Search for a product</div>
                <div className="text-sm mt-2">Enter description, PLU, barcode, or supplier PLU</div>
                <div className="text-sm mt-1">Press Enter to search</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Subtle */}
        <div className="bg-gray-50 px-4 py-3 text-center text-xs text-gray-400 border-t">
          {selectedProduct 
            ? "Press ESC to go back • Click header arrow to return"
            : "Press ESC to close • Enter to search"
          }
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PriceCheckModal;
